import { action, computed, observable, makeAutoObservable } from 'mobx';
import { ReduxAction } from 'src/AppState/ReduxAction';
import { ModelsState } from 'src_common/common/websocket2/ModelsState';
import moment from 'moment';
import {
    CompetitionItemViewType,
    EventsCollectionQueryModel,
} from 'src_common/common/websocket2/models/EventsCollectionQueryModel';
import { EventsCollectionQueryType } from 'src_common/common/websocket2/modelsApi/EventsCollectionQuery';
import { FiltersType } from 'src/domains/sportsbook/trading/view/Liabilities/Liabilities.types';

export class LiabilitiesState {
    @observable rangeStartDate: string;
    @observable rangeEndDate: string;

    @observable sport: string | null;
    @observable country: string | null;
    @observable q: string | null;
    @observable competition: number | null;

    constructor(
        public readonly reduxAction: ReduxAction,
        private readonly websocketV2: ModelsState
    ) {
        this.rangeStartDate = moment().startOf('day').toISOString();
        this.rangeEndDate = moment().endOf('day').add(7, 'days').toISOString();

        this.sport = null;
        this.country = null;
        this.q = null;
        this.competition = null;
        makeAutoObservable(this);
    }

    @computed get filters(): FiltersType {
        return {
            range: {
                startDate: this.rangeStartDate,
                endDate: this.rangeEndDate,
            },
            sport: this.sport,
            country: this.country,
            q: this.q,
            competition: this.competition,
        };
    }

    @computed private get allEventsList(): EventsCollectionQueryModel | null {
        const query: EventsCollectionQueryType = {
            'startTime[from]': this.rangeStartDate,
            'startTime[to]': this.rangeEndDate,
        };

        return this.websocketV2.getEventQuery(query);
    }

    @computed private get filterBySport(): EventsCollectionQueryModel | null {
        const list = this.allEventsList;

        if (this.sport === null || list === null) {
            return this.allEventsList;
        }

        return list.filterBySport((sportId: string): boolean => {
            return this.sport === sportId;
        });
    }

    @computed private get filterByCountry(): EventsCollectionQueryModel | null {
        const list = this.filterBySport;

        if (this.country === null || list === null) {
            return list;
        }

        return list.filterBySportAndEvent((_sportId: string, event): boolean => {
            return event.tags['country'] === this.country;
        });
    }

    @computed private get filterByQ(): EventsCollectionQueryModel | null {
        const list = this.filterByCountry;

        if (list === null || this.q === null || this.q.length < 2) {
            return list;
        }

        return list.filterBySearchText(this.q);
    }

    @computed
    private get filterByCompetition(): EventsCollectionQueryModel | null {
        const list = this.filterByQ;

        if (this.competition === null || list === null) {
            return list;
        }

        return list.filterByCompetition((competitionId: number): boolean => {
            return this.competition === competitionId;
        });
    }

    private get allEvents(): EventsCollectionQueryModel | null {
        return this.filterByCompetition;
    }

    public get isLoading(): boolean {
        return this.allEvents === null;
    }

    public get list(): Array<number> {
        if (this.allEvents === null) {
            return [];
        }

        const allEvents = this.allEvents.events;

        allEvents.sort((a, b) => a.startTime - b.startTime);

        return allEvents.map((item) => item.id);
    }

    public get competitionList(): Array<CompetitionItemViewType> {
        if (this.allEvents === null) {
            return [];
        }

        return this.allEvents.competitionForViewSortByName;
    }

    @action public setFilterForField = (field: string, value: any): void => {
        switch (field) {
            case 'range':
                this.rangeStartDate = value.startDate;
                this.rangeEndDate = value.endDate;
                break;
            case 'sport':
                this.sport = value;
                break;
            case 'country':
                this.country = value;
                break;
            case 'competition':
                this.competition = value;
                break;
            case 'q':
                this.q = value;
                break;
            default:
                return;
        }
    };
}
